<template>
  <TheSpinner v-if="isTheSpinnerVisible" />
  <Toast
    position="bottom-right"
    :base-z-index="1"
  />
  <FeatureUnavailableModal />
  <MigrationV5Modal />
  <router-view v-slot="{ Component }">
    <component :is="Component" />
  </router-view>
  <AIModal />
</template>

<script lang="ts">
import {
  provide,
  defineComponent,
  computed,
  onMounted,
  onUnmounted,
} from 'vue';
import Toast from 'primevue/toast';

import { TenantAttributes } from '@/types';
import { useStore } from '@/store';
import TheSpinner from '@/components/TheSpinner.vue';
import FeatureUnavailableModal from '@/components/modals/FeatureUnavailableModal.vue';
import AIModal from '@/components/modals/AIModal.vue';
import MigrationV5Modal from '@/components/migration-v5/MigrationV5Modal.vue';
import { UserState } from '@/composables/User';
import {
  fetchMigrationV5Status,
  shouldRedirectToV5,
  redirectToV5,
  isModalSnoozed,
  openMigrationV5Modal,
} from '@/composables/migrationV5';

const MIGRATION_V5_POLL_MS = 60_000;

/**
 * Polling de fond pour détecter un changement de statut migration V5
 * pendant qu'une session V4 est active. Le 1er check est fait par le
 * router guard (cf. router/index.ts → checkMigrationV5OnGuard), donc
 * on n'a PAS de check immédiat ici — le polling 60s suffit comme
 * filet de sécurité pour les onglets ouverts en continu.
 */

export default defineComponent({
  name: 'App',
  components: {
    AIModal,
    TheSpinner,
    Toast,
    FeatureUnavailableModal,
    MigrationV5Modal,
  },

  setup() {
    const tenantAttributes: TenantAttributes = {
      brandColors: null,
      logo: '',
    };
    provide('tenantAttributes', tenantAttributes);
    const store = useStore();
    const isTheSpinnerVisible = computed(() => store.state.general.isTheSpinnerVisible);

    let migrationV5Timer: number | null = null;

    const checkMigrationV5 = async () => {
      if (!UserState.isAuthenticated || !UserState.activeShop?.id) return;
      const status = await fetchMigrationV5Status();
      if (shouldRedirectToV5(status)) {
        redirectToV5();
        return;
      }
      if (status === 'invited' && !isModalSnoozed()) {
        openMigrationV5Modal();
      }
    };

    onMounted(() => {
      migrationV5Timer = window.setInterval(() => {
        if (document.visibilityState === 'visible') {
          checkMigrationV5().catch(() => { /* ignore */ });
        }
      }, MIGRATION_V5_POLL_MS);
    });

    onUnmounted(() => {
      if (migrationV5Timer !== null) {
        window.clearInterval(migrationV5Timer);
        migrationV5Timer = null;
      }
    });

    return {
      isTheSpinnerVisible,
    };
  },
});
</script>
